import { Input } from 'antd';
import { FormattedMessage } from 'react-intl';
import { LikelihoodOfCompletion } from '../../../api/btlApplicationForm/conveyancingTypes';
import { Conveyancing } from '../../../api/btlApplicationForm/conveyancingTypes';
import { formatDate, formatDateTimeLondon } from '../../../utils/dates';

const formatLikelihoodOfCompletion = (likelihood?: LikelihoodOfCompletion | null): string => {
  if (!likelihood) return 'None';
  const likelihoodMap: Record<LikelihoodOfCompletion, string> = {
    [LikelihoodOfCompletion.ESTATE_AGENT_TARGET]: 'Estate Agent Target',
    [LikelihoodOfCompletion.CUSTOMER_TARGET]: 'Customer Target',
    [LikelihoodOfCompletion.EXCHANGE]: 'Exchange',
    [LikelihoodOfCompletion.NOTICE_TO_COMPLETE]: 'Notice To Complete',
    [LikelihoodOfCompletion.SOLICITORS_TARGET]: 'Solicitors Target',
  };
  return likelihoodMap[likelihood] || likelihood;
};

interface PostOfferProps {
  conveyancing: Conveyancing | null | undefined;
}

export const PostOffer = ({ conveyancing }: PostOfferProps) => {
  const conveyancingData = conveyancing || null;

  return (
    <div className={'w-full'}>
      <h5 className={'mt-0 mb-4'}>
        <FormattedMessage id={'applicationDetails.postOffer.title'} />
      </h5>

      <div className={'grid grid-cols-1 md:grid-cols-2 gap-6'}>
        <div>
          <label className={'block text-sm font-medium text-gray-700 mb-2'}>
            <FormattedMessage id={'btlApplicationForm.POST_OFFER.offerSignedDate.label'} />
          </label>
          <Input
            value={formatDate(conveyancingData?.offerSignedAt)}
            variant={'filled'}
            readOnly
            className={'pointer-events-none'}
          />
        </div>

        <div>
          <label className={'block text-sm font-medium text-gray-700 mb-2'}>
            <FormattedMessage id={'btlApplicationForm.POST_OFFER.productFeesPaidDate.label'} />
          </label>
          <Input
            value={formatDateTimeLondon(conveyancingData?.productFeesPaidAt)}
            variant={'filled'}
            readOnly
            className={'pointer-events-none'}
          />
        </div>

        <div>
          <label className={'block text-sm font-medium text-gray-700 mb-2'}>
            <FormattedMessage id={'btlApplicationForm.POST_OFFER.offerExpiryDate.label'} />
          </label>
          <Input
            value={formatDate(conveyancingData?.offerExpiresAt)}
            variant={'filled'}
            readOnly
            className={'pointer-events-none'}
          />
        </div>

        <div>
          <label className={'block text-sm font-medium text-gray-700 mb-2'}>
            <FormattedMessage id={'btlApplicationForm.POST_OFFER.undertakingPaidDate.label'} />
          </label>
          <Input
            value={formatDate(conveyancingData?.undertakingPaidAt)}
            variant={'filled'}
            readOnly
            className={'pointer-events-none'}
          />
        </div>

        <div>
          <label className={'block text-sm font-medium text-gray-700 mb-2'}>
            <FormattedMessage id={'btlApplicationForm.POST_OFFER.propertyTitleNumber.label'} />
          </label>
          <Input
            value={conveyancingData?.propertyTitleNumber || ''}
            variant={'filled'}
            readOnly
            className={'pointer-events-none'}
          />
        </div>

        <div>
          <label className={'block text-sm font-medium text-gray-700 mb-2'}>
            <FormattedMessage id={'btlApplicationForm.POST_OFFER.contributionStatementApproved.label'} />
          </label>
          <Input
            value={formatDate(conveyancingData?.contributionStatementApprovedAt)}
            variant={'filled'}
            readOnly
            className={'pointer-events-none'}
          />
        </div>

        <div>
          <label className={'block text-sm font-medium text-gray-700 mb-2'}>
            <FormattedMessage id={'btlApplicationForm.POST_OFFER.financeDocsSentForSignature.label'} />
          </label>
          <Input
            value={formatDate(conveyancingData?.financeDocsSentForSignatureAt)}
            variant={'filled'}
            readOnly
            className={'pointer-events-none'}
          />
        </div>

        <div>
          <label className={'block text-sm font-medium text-gray-700 mb-2'}>
            <FormattedMessage id={'btlApplicationForm.POST_OFFER.financeDocsSigned.label'} />
          </label>
          <Input
            value={formatDate(conveyancingData?.financeDocsSignedAt)}
            variant={'filled'}
            readOnly
            className={'pointer-events-none'}
          />
        </div>

        <div>
          <label className={'block text-sm font-medium text-gray-700 mb-2'}>
            <FormattedMessage id={'btlApplicationForm.POST_OFFER.targetCompletion.label'} />
          </label>
          <Input
            value={formatDate(conveyancingData?.targetCompletionAt)}
            variant={'filled'}
            readOnly
            className={'pointer-events-none'}
          />
        </div>

        <div>
          <label className={'block text-sm font-medium text-gray-700 mb-2'}>
            <FormattedMessage id={'btlApplicationForm.POST_OFFER.likelihoodOfCompletion.label'} />
          </label>
          <Input
            value={formatLikelihoodOfCompletion(conveyancingData?.likelihoodOfCompletion)}
            variant={'filled'}
            readOnly
            className={'pointer-events-none'}
          />
        </div>

        <div>
          <label className={'block text-sm font-medium text-gray-700 mb-2'}>
            <FormattedMessage id={'btlApplicationForm.POST_OFFER.finalCompletionPackReceived.label'} />
          </label>
          <Input
            value={formatDate(conveyancingData?.finalCompletionPackReceivedAt)}
            variant={'filled'}
            readOnly
            className={'pointer-events-none'}
          />
        </div>

        <div>
          <label className={'block text-sm font-medium text-gray-700 mb-2'}>
            <FormattedMessage id={'btlApplicationForm.POST_OFFER.fundsInstructed.label'} />
          </label>
          <Input
            value={formatDateTimeLondon(conveyancingData?.fundsInstructedAtTime)}
            variant={'filled'}
            readOnly
            className={'pointer-events-none'}
          />
        </div>

        <div>
          <label className={'block text-sm font-medium text-gray-700 mb-2'}>
            <FormattedMessage id={'btlApplicationForm.POST_OFFER.startDate.label'} />
          </label>
          <Input
            value={formatDate(conveyancingData?.startDate)}
            variant={'filled'}
            readOnly
            className={'pointer-events-none'}
          />
        </div>

        <div>
          <label className={'block text-sm font-medium text-gray-700 mb-2'}>
            <FormattedMessage id={'btlApplicationForm.POST_OFFER.fundsReleased.label'} />
          </label>
          <Input
            value={formatDateTimeLondon(conveyancingData?.fundsReleasedAtTime)}
            variant={'filled'}
            readOnly
            className={'pointer-events-none'}
          />
        </div>

        <div>
          <label className={'block text-sm font-medium text-gray-700 mb-2'}>
            <FormattedMessage id={'btlApplicationForm.POST_OFFER.completionDate.label'} />
          </label>
          <Input
            value={formatDate(conveyancingData?.completionDate)}
            variant={'filled'}
            readOnly
            className={'pointer-events-none'}
          />
        </div>
      </div>
    </div>
  );
};
